/**
 * Utility functions for Google Workspace and Google Sheets integration error handling
 * and Google Picker loader.
 */

/**
 * Parses and formats Google Workspace specific API errors according to backend guide:
 * - 401 Unauthorized: Auth token expired or user not connected to Google
 * - 400 Bad Request: Missing spreadsheet_id, invalid sheet tab, or empty data
 * - 502 Bad Gateway: Google API service error or network failure with Google
 *
 * @param {any} error - Axios error object
 * @returns {{ message: string, isAuthError: boolean, isGatewayError: boolean, isBadRequest: boolean, detail: string|null }}
 */
/** Open Google OAuth in a popup. Full-page redirect if popup blocked. Never iframe. */
export function openGoogleAuthWindow(authUrl) {
  const width = 600;
  const height = 700;
  const left = Math.max(0, Math.round((window.screen.width - width) / 2));
  const top = Math.max(0, Math.round((window.screen.height - height) / 2));
  const features = `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`;
  const popup = window.open(authUrl, 'GoogleWorkspaceAuth', features);
  if (!popup) {
    window.location.href = authUrl;
    return null;
  }
  popup.focus();
  return popup;
}

export function openSpreadsheetUrl(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function parseGoogleWorkspaceError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const detail = data?.detail || data?.message || null;

  if (status === 401) {
    return {
      message:
        'Your Google Workspace connection has expired or is disconnected. Please reconnect your account.',
      isAuthError: true,
      isGatewayError: false,
      isBadRequest: false,
      detail,
    };
  }

  if (status === 502) {
    return {
      message:
        'Google Workspace service temporarily unreachable. Please try again.',
      isAuthError: false,
      isGatewayError: true,
      isBadRequest: false,
      detail: detail || '502 Bad Gateway',
    };
  }

  if (status === 400) {
    return {
      message:
        detail ||
        'Invalid spreadsheet or request parameters. Please verify your selection.',
      isAuthError: false,
      isGatewayError: false,
      isBadRequest: true,
      detail,
    };
  }

  return {
    message:
      detail ||
      error?.message ||
      'An unexpected error occurred while communicating with Google Workspace.',
    isAuthError: false,
    isGatewayError: false,
    isBadRequest: false,
    detail,
  };
}

/**
 * Loads the Google API client script (gapi) dynamically if not already present.
 * @returns {Promise<any>}
 */
export function googleErrorText(error) {
  const detail =
    error?.response?.data?.detail ?? error?.response?.data?.message;
  if (Array.isArray(detail)) return detail.map(String).join(' ');
  if (detail && typeof detail === 'object') return detail.message || '';
  return detail ? String(detail) : '';
}

export function googleErrorCode(error) {
  const data = error?.response?.data;
  return data?.code || data?.error_code || null;
}

export function isDriveFileAccessDenied(error) {
  return (
    error?.response?.status === 400 &&
    /drive file access was not granted/i.test(googleErrorText(error))
  );
}

/** Picker session failures. Branch on `code`, not HTTP status alone. */
export function pickerSessionErrorMessage(error) {
  const status = error?.response?.status;
  const code = googleErrorCode(error);
  if (
    (status === 404 && code === 'google_workspace_not_connected') ||
    (status === 400 && code === 'google_workspace_disconnected')
  ) {
    return 'Google Workspace is not connected.';
  }
  if (
    status === 400 &&
    code === 'google_workspace_api_error' &&
    /drive file access was not granted/i.test(googleErrorText(error))
  ) {
    return `${DRIVE_CHECKBOX_HINT} Disconnect Google, then connect again.`;
  }
  return null;
}

export const DRIVE_CHECKBOX_HINT =
  'Drive file access was not granted. Connect again and leave the Drive checkbox on.';

/**
 * Google Picker dialog. setVisible only. Never toUri() into an iframe.
 */
export function openSpreadsheetPicker({
  accessToken,
  appId,
  apiKey,
  onPicked,
  mimeTypes,
}) {
  return new Promise((resolve, reject) => {
    if (!window.gapi) {
      reject(new Error('Google API client is unavailable.'));
      return;
    }
    window.gapi.load('picker', {
      callback: () => {
        try {
          if (!window.google?.picker) {
            reject(new Error('Google Picker library failed to initialize.'));
            return;
          }
          const pickerApi = window.google.picker;
          const view = new pickerApi.View(pickerApi.ViewId.SPREADSHEETS);
          if (mimeTypes) view.setMimeTypes(mimeTypes);
          const picker = new pickerApi.PickerBuilder()
            .addView(view)
            .setOAuthToken(accessToken)
            .setDeveloperKey(apiKey)
            .setAppId(String(appId))
            .setCallback((data) => {
              const action = data.action ?? data[pickerApi.Response.ACTION];
              if (action !== pickerApi.Action.PICKED) return;
              const docs = data.docs ?? data[pickerApi.Response.DOCUMENTS];
              const file = docs?.[0];
              if (!file) return;
              const id = file.id ?? file[pickerApi.Document.ID];
              if (!id) return;
              onPicked({
                id,
                name:
                  file.name ?? file[pickerApi.Document.NAME] ?? 'Spreadsheet',
              });
            })
            .build();
          picker.setVisible(true);
          resolve(picker);
        } catch (err) {
          reject(err);
        }
      },
      onerror: () => reject(new Error('Failed to load Google Picker.')),
    });
  });
}

export function loadGoogleApiScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('Window is not defined'));
    }

    if (window.gapi && window.google?.picker) {
      return resolve(window.gapi);
    }

    const SCRIPT_ID = 'google-api-script';
    let script = document.getElementById(SCRIPT_ID);

    if (script) {
      if (window.gapi) {
        return resolve(window.gapi);
      }
      script.addEventListener('load', () => resolve(window.gapi));
      script.addEventListener('error', (err) => reject(err));
      return;
    }

    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.gapi);
    script.onerror = (err) =>
      reject(new Error('Failed to load Google API client script: ' + err));
    document.body.appendChild(script);
  });
}
